/**
 * Persist learner catalog filters until logout (or until the user changes them).
 * Survive navigation and refresh; clear on sign-out so the next account starts clean.
 */

import {
  DEFAULT_INBOX_FILTERS,
  type InboxFilterState
} from '../services/messageService';
import {
  LibraryBookDifficulty,
  LibraryBookType
} from '../services/libraryService';

const STORAGE_PREFIX = 'learner-filters:v1:';

export type CatalogFilterKey = 'all' | 'done' | 'remaining';

export type PersistedLibraryFilters = {
  searchInput: string;
  reviewedFilter: boolean | null;
  bookTypeFilter: LibraryBookType | null;
  difficultyFilter: LibraryBookDifficulty | null;
  categoryFilter: number | null;
};

export type PersistedCatalogFlowFilters = {
  filter: CatalogFilterKey;
  courseFilter: number | null;
};

const DEFAULT_LIBRARY_FILTERS: PersistedLibraryFilters = {
  searchInput: '',
  reviewedFilter: null,
  bookTypeFilter: null,
  difficultyFilter: null,
  categoryFilter: null
};

const DEFAULT_CATALOG_FLOW_FILTERS: PersistedCatalogFlowFilters = {
  filter: 'all',
  courseFilter: null
};

function currentUserSuffix(): string | null {
  try {
    const raw = localStorage.getItem('userProfile');
    if (!raw) return null;
    const profile = JSON.parse(raw) as { id?: unknown };
    if (profile?.id == null) return null;
    return String(profile.id);
  } catch {
    return null;
  }
}

function storageKey(slot: string): string | null {
  const user = currentUserSuffix();
  if (!user) return null;
  return `${STORAGE_PREFIX}${user}:${slot}`;
}

function readJson<T>(slot: string, fallback: T, validate: (value: unknown) => value is T): T {
  const key = storageKey(slot);
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return validate(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(slot: string, value: unknown): void {
  const key = storageKey(slot);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode */
  }
}

function isNullOrBookType(v: unknown): v is LibraryBookType | null {
  return v === null || v === LibraryBookType.Classic || v === LibraryBookType.New;
}

function isNullOrDifficulty(v: unknown): v is LibraryBookDifficulty | null {
  return (
    v === null ||
    v === LibraryBookDifficulty.Simple ||
    v === LibraryBookDifficulty.Intermediate ||
    v === LibraryBookDifficulty.Advanced
  );
}

function isLibraryFilters(v: unknown): v is PersistedLibraryFilters {
  if (!v || typeof v !== 'object') return false;
  const o = v as PersistedLibraryFilters;
  return (
    typeof o.searchInput === 'string' &&
    (o.reviewedFilter === null || typeof o.reviewedFilter === 'boolean') &&
    isNullOrBookType(o.bookTypeFilter) &&
    isNullOrDifficulty(o.difficultyFilter) &&
    (o.categoryFilter === null || (typeof o.categoryFilter === 'number' && Number.isFinite(o.categoryFilter)))
  );
}

function isInboxFilters(v: unknown): v is InboxFilterState {
  if (!v || typeof v !== 'object') return false;
  const o = v as InboxFilterState;
  const readOk = o.read === 'all' || o.read === 'read' || o.read === 'unread';
  const dateOk = o.date === 'all' || o.date === 'week' || o.date === 'month' || o.date === 'sixMonths';
  return readOk && dateOk;
}

function isCatalogFlowFilters(v: unknown): v is PersistedCatalogFlowFilters {
  if (!v || typeof v !== 'object') return false;
  const o = v as PersistedCatalogFlowFilters;
  const filterOk = o.filter === 'all' || o.filter === 'done' || o.filter === 'remaining';
  const courseOk = o.courseFilter === null || (typeof o.courseFilter === 'number' && Number.isFinite(o.courseFilter));
  return filterOk && courseOk;
}

export function readPersistedLibraryFilters(): PersistedLibraryFilters {
  return readJson('library', DEFAULT_LIBRARY_FILTERS, isLibraryFilters);
}

export function writePersistedLibraryFilters(value: PersistedLibraryFilters): void {
  writeJson('library', value);
}

export function readPersistedInboxFilters(): InboxFilterState {
  return readJson('inbox', DEFAULT_INBOX_FILTERS, isInboxFilters);
}

export function writePersistedInboxFilters(value: InboxFilterState): void {
  writeJson('inbox', value);
}

export function readPersistedCatalogFlowFilters(slot: 'quiz' | 'exercise'): PersistedCatalogFlowFilters {
  return readJson(slot, DEFAULT_CATALOG_FLOW_FILTERS, isCatalogFlowFilters);
}

export function writePersistedCatalogFlowFilters(
  slot: 'quiz' | 'exercise',
  value: PersistedCatalogFlowFilters
): void {
  writeJson(slot, value);
}

export function clearLearnerPersistedFilters(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}
